/**
 * bb-availability.js
 * ───────────────────────────────────────────────────────────────────────────
 * Region-gated Add to Cart (spec extension). Gates the PDP buy buttons by the
 * visitor's resolved region against the launch scope (served regions A–C), and
 * reveals a notify/contact panel for visitors we positively can't serve. Hiding
 * is done by toggling a class (not disabling the native control) so Horizon's
 * own sold-out logic stays intact; the location CTA opens the shared modal.
 *
 * Launch scope = served regions A–C. Three visitor states:
 *   - SERVED (region ∈ A–C):
 *       · region ∈ product regions → available (Add to Cart)
 *       · region ∉ product regions / UNTAGGED → not available → location CTA
 *   - UNKNOWN (served == null — geo failed/denied/pending, theme editor) →
 *       FAIL OPEN: Add to Cart stays, plus a soft nudge to set location. We do
 *       NOT block a sale just because detection was inconclusive.
 *   - KNOWN-UNSERVED (served === false — D–I / AK-HI / non-US) → buy buttons
 *       hidden and the "we don't serve your area yet" panel (set-location +
 *       notify-me email capture + contact link) is revealed.
 *
 * Gating is applied only AFTER location resolves, so ATC never flashes to the
 * CTA while detection is still pending. Toggling a class (not disabling the
 * native control) keeps Horizon's own sold-out logic intact.
 */

import { getRegionTags, isServedRegion, getStateName } from '@theme/bb-region-config';
import { getLocation } from '@theme/bb-location-store';
import { BB_LOCATION_EVENT, getLastResolved } from '@theme/bb-geo-detect';
import { BB_OPEN_ZIP_MODAL } from '@theme/bb-location-banner';

// Main buy buttons + quick-add forms + the sticky add-to-cart bar.
const FORM_SELECTOR = 'product-form-component[data-regions], sticky-add-to-cart[data-regions]';
const UNSERVED_PANEL_SELECTOR = '[data-bb-unserved-panel]';

let _region = null;
let _served = null; // true = served (A–C), false = known-unserved, null = unknown
let _resolved = false;

function isBuyable(el) {
  // Served region: buyable only if the product is tagged for it (untagged fails
  // closed → location CTA). Unknown: fail OPEN (buyable). Known-unserved: not.
  if (isServedRegion(_region)) return getRegionTags(el).includes(_region);
  return _served !== false;
}

function gateOne(el) {
  el.classList.toggle('bb-out-of-region', !isBuyable(el));
}

function apply() {
  if (!_resolved || typeof document === 'undefined') return;
  // Reveal the buy area now that the gate is decided (clears the flash-guard set
  // by bb-flash-guard.liquid on product pages). Idempotent across re-applies.
  document.documentElement.setAttribute('data-bb-availability', 'ready');
  for (const el of document.querySelectorAll(FORM_SELECTOR)) gateOne(el);
  // Reveal the notify/contact panel ONLY for a positively-unserved visitor — not
  // for an unknown one (who can still buy) nor a served-but-untagged product.
  const unserved = _served === false;
  for (const panel of document.querySelectorAll(UNSERVED_PANEL_SELECTOR)) {
    panel.toggleAttribute('hidden', !unserved);
  }
  if (unserved) personalizeNotify();
}

/** Stamp the notify form's hidden body with the visitor's detected area, so the
 *  store owner's contact email says which region to add to the waitlist. */
function personalizeNotify() {
  const loc = getLocation();
  const area = loc && loc.state ? getStateName(loc.state) || loc.state : null;
  if (!area) return;
  for (const field of document.querySelectorAll('[data-bb-notify-area]')) {
    field.value = `Notify me when Bower & Branch starts delivering to ${area}.`;
  }
}

/** Gate quick-add forms that arrive later (infinite scroll / facet re-render). */
function startObserver() {
  if (typeof MutationObserver === 'undefined' || !document.body) return;
  const observer = new MutationObserver((mutations) => {
    if (!_resolved) return;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches && node.matches(FORM_SELECTOR)) gateOne(node);
        node.querySelectorAll && node.querySelectorAll(FORM_SELECTOR).forEach(gateOne);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function onResolved(detail) {
  _region = detail && detail.region != null ? detail.region : null;
  _served = detail && typeof detail.served === 'boolean' ? detail.served : null;
  _resolved = true;
  apply();
}

function boot() {
  if (typeof document === 'undefined') return;

  document.addEventListener(BB_LOCATION_EVENT, (event) => onResolved(event.detail));
  startObserver();

  // The "update your ZIP for availability" CTA opens the shared location modal.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-bb-region-cta]')) {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent(BB_OPEN_ZIP_MODAL));
    }
  });

  // Skip the cached path in the theme editor: detection emits an unknown
  // (fail-open) result in design mode, and applying a stale live-store cache here
  // would briefly hide a merchant's Add to Cart before that event corrects it.
  const inDesignMode = typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode;
  if (!inDesignMode) {
    // Apply against an already-known location (returning visitor / earlier resolve).
    const cached = getLocation();
    if (cached) {
      onResolved(cached);
      return;
    }
  }
  const last = getLastResolved();
  if (last !== undefined) onResolved(last);
}

boot();
